import { useCallback, useMemo } from 'react';
import { Button } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { GET_ATTRACTIONS_BY_IDS } from '@queries/attractions';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import type { ItineraryItem } from '@src/types/itinerary';

const GOOGLE_API_KEY = import.meta.env.GOOGLE_API_KEY || '';
const GOOGLE_MAP_ID = import.meta.env.GOOGLE_MAP_ID || import.meta.env.VITE_GOOGLE_MAP_ID || ''; // NEW

type Place = {
  id: string;
  name?: string | null;
  googlePlaceId: string;
};

type BadgeMeta = { day: number; slot: 'AM' | 'PM'; title?: string | null };

export type ItineraryMapProps = {
  itinerary?: ItineraryItem[];
  iconUrl?: string;
  buttonLabel?: string;
  className?: string;
  disabled?: boolean;
};

// Accept activity meta and inject into the left panel
function openGoogleMapInNewTab(
  places: Place[],
  apiKey: string,
  iconUrl?: string,
  title?: string,
  activityMeta?: Record<string, BadgeMeta[]>,
  mapId?: string
) {
  const win = window.open('', '_blank');
  if (!win) return;

  const safePlacesJson = JSON.stringify(places).replace(/</g, '\\u003c');
  const safeMetaJson = JSON.stringify(activityMeta || {}).replace(/</g, '\\u003c');

  const doc = win.document;
  doc.open();
  doc.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${title ? `${title} - ` : ''}Attractions Map</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    :root { --panel-w: 340px; }
    html, body { height: 100%; margin: 0; }
    #app { position: fixed; inset: 0; display: grid; grid-template-columns: var(--panel-w) 1fr; }
    #panel { border-right: 1px solid #e9ecef; background: #fff; overflow: auto; font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
    #panelHeader { position: sticky; top: 0; z-index: 1; background: #fff; padding: 10px 12px; border-bottom: 1px solid #eee; display: grid; grid-template-columns: 1fr min-content; gap: 8px; align-items: center; }
    #search { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
    #actions { display: flex; gap: 8px; align-items: center; }
    .btn { font-size: 12px; padding: 6px 10px; border-radius: 8px; border: 1px solid #ddd; background: #f8f9fa; cursor: pointer; }
    #list { padding: 8px 6px 16px; }
    .item { padding: 10px 10px; border-radius: 10px; border: 1px solid #eee; margin: 8px 6px; display: grid; gap: 6px; cursor: pointer; }
    .item:hover { background: #fafafa; }
    .title { font-weight: 600; font-size: 14px; }
    .sub { color: #666; font-size: 12px; }
    .badges { display:flex; gap:6px; flex-wrap:wrap; }
    .badge { display:inline-flex; align-items:center; gap:6px; padding: 3px 8px; border-radius: 999px; border: 1px solid #e5e7eb; background:#f9fafb; font-size: 11px; color:#374151; }
    .badge .dot { width:6px; height:6px; border-radius:999px; background:#94a3b8; }
    .badge.am .dot { background:#0ea5e9; }
    .badge.pm .dot { background:#f59e0b; }
    #map { width: 100%; height: 100%; }

    /* For AdvancedMarkerElement */
    .gti-pin {
      position: relative;
      width: 44px;
      height: 58px;
      background: no-repeat center/contain;
      overflow: visible; /* allow badge to render outside */
    }
    .gti-pin-badge {
      position: absolute;
      left: 50%;
      bottom: calc(100% + 3px);
      transform: translateX(-50%);
      padding: 3px 9px;
      line-height: 1;
      font-size: 15px;
      font-weight: 700;
      color: #fff;
      border-radius: 15px;
      border: 1px solid rgba(255,255,255,0.8);
      background: #6366f1; /* unified purple */
      box-shadow: 0 1px 2px rgba(0,0,0,0.25);
      white-space: nowrap;
      pointer-events: none;
    }
    .gti-pin-badge.am { background: #6366f1; }
    .gti-pin-badge.pm { background: #6366f1; }
    .gti-pin-badge.multi { background: #6366f1; }

    @media (max-width: 520px) { #panelHeader { grid-template-columns: 1fr; } #actions { justify-content: flex-end; } }
  </style>
</head>
<body>
  <div id="app">
    <div id="panel">
      <div id="panelHeader">
        <input id="search" placeholder="Search places" />
        <div id="actions">
          <button class="btn" id="showAllBtn" title="Show all places on the map">Show All</button>
        </div>
      </div>
      <div id="list"></div>
    </div>
    <div id="map"></div>
  </div>

  <script>
    const PLACES = ${safePlacesJson};
    const META = ${safeMetaJson};
    const ICON_URL_IN = ${JSON.stringify(iconUrl || '')};
    const MAP_ID = ${JSON.stringify(mapId || '')};

    function esc(s){return String(s||'').replace(/[&<>"']/g,m=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\\'':'&#39;'}[m]))}

    let ICON_URL = '';
    if (ICON_URL_IN) {
      try { const img = new Image(); img.onload = () => { ICON_URL = ICON_URL_IN; }; img.onerror = () => { ICON_URL = ''; }; img.src = ICON_URL_IN; } catch (e) {}
    }

    function createInfoContent(detail) {
      const name = detail?.displayName || detail?.name || detail?.displayName?.text || 'Place';
      const ratingCount = detail?.user_ratings_total ?? detail?.userRatingCount;
      const rating = detail?.rating ? '★ ' + detail.rating.toFixed(1) + (ratingCount ? ' (' + ratingCount + ')' : '') : '';
      const address = detail?.formatted_address || detail?.formattedAddress || '';
      const photoObj = (detail?.photos && detail.photos[0]) || null;
      const photoUrl = photoObj ? (photoObj.getURI ? photoObj.getURI({ maxWidth: 340, maxHeight: 200 }) : (photoObj.getUrl ? photoObj.getUrl({ maxWidth: 340, maxHeight: 200 }) : '')) : '';
      const gmUrl = 'https://maps.google.com/?q=place_id:' + (detail?.id || detail?.place_id);
      return (
        '<div style="max-width:340px">' +
          (photoUrl ? '<img src="' + photoUrl + '" alt="" style="width:100%;border-radius:8px;margin-bottom:8px"/>' : '') +
          '<div style="font-weight:700;margin:0 0 4px 0;font-size:14px;">' + esc(name) + '</div>' +
          (rating ? '<div style="color:#444;margin-bottom:4px;font-size:12px">' + rating + '</div>' : '') +
          (address ? '<div style="color:#666;margin-bottom:8px;font-size:12px">' + esc(address) + '</div>' : '') +
          '<div style="display:flex;gap:8px;flex-wrap:wrap"><a href="' + gmUrl + '" target="_blank" rel="noopener" style="text-decoration:none;border:1px solid #ddd;padding:6px 10px;border-radius:8px;background:#f8f9fa;font-size:12px">Open in Google Maps</a></div>' +
        '</div>'
      );
    }

    function overlayMeta(pid) {
      const arr = META[pid] || [];
      if (!arr.length) return { text: '', cls: '', glyph: '' };

      // Normalize and sort
      const sorted = arr.slice().sort((a, b) => a.day - b.day || (a.slot === 'AM' ? -1 : 1));

      // Group by day -> Set<slot>
      const daySlots = new Map();
      for (const m of sorted) {
        if (!daySlots.has(m.day)) daySlots.set(m.day, new Set());
        daySlots.get(m.day).add(m.slot);
      }
      const days = Array.from(daySlots.keys()).sort((a, b) => a - b);

      // Helper: are days consecutive?
      const consecutive = days.every((d, i) => i === 0 || d === days[0] + i);

      // Helper: slot summary across all entries
      const allAM = sorted.every((m) => m.slot === 'AM');
      const allPM = sorted.every((m) => m.slot === 'PM');
      const slotSuffix = allAM ? ' AM' : allPM ? ' PM' : '';

      // Single day cases
      if (days.length === 1) {
        const d = days[0];
        const slots = daySlots.get(d);
        if (slots.size === 1) {
          const s = slots.has('AM') ? 'AM' : 'PM';
          return { text: 'D' + d + ' ' + s, cls: s === 'AM' ? 'am' : 'pm', glyph: String(d) + (s === 'AM' ? 'A' : 'P') };
        }
        // both AM and PM on same day
        return { text: 'D' + d + ' AM/PM', cls: 'multi', glyph: String(arr.length) + '×' };
      }

      // Multiple days
      let dayLabel = '';
      if (consecutive) {
        const first = days[0], last = days[days.length - 1];
        dayLabel = 'D' + first + '-' + last;
      } else {
        if (days.length <= 3) {
          dayLabel = 'D' + days.join(',');
        } else {
          dayLabel = 'D' + days.slice(0, 2).join(',') + '+';
        }
      }

      // If all slots same across days, append AM/PM; otherwise omit for brevity
      const text = dayLabel + slotSuffix;
      const cls = allAM ? 'am' : allPM ? 'pm' : 'multi';
      return { text, cls, glyph: String(arr.length) + '×' };
    }

    // Fallback: compose an icon with a badge on a canvas (no Map ID required)
    function loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    }
    // Return composed data URL + dimensions + anchor for the standard Marker
    async function composeIcon(baseUrl, meta) {
      const pinW = 44, pinH = 58;
      const gap = 3, bh = 24; // vertical badge height above the pin
      const font = '700 15px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
      const padX = 9;
      const sideMargin = 8;

      const text = meta?.text || '';

      // Measure text to compute required badge width
      let measuredTextW = 0;
      if (text) {
        const tctx = document.createElement('canvas').getContext('2d');
        if (tctx) {
          tctx.font = font;
          measuredTextW = Math.ceil(tctx.measureText(text).width);
        }
      }
      const desiredBadgeW = text ? measuredTextW + padX * 2 : 0;

      // Canvas widens to fit the badge (with a small margin), otherwise stays pin width
      const canvasW = Math.max(pinW, desiredBadgeW ? desiredBadgeW + sideMargin : pinW);
      const extraTop = text ? gap + bh : 0;
      const canvasH = pinH + extraTop;

      const c = document.createElement('canvas');
      c.width = canvasW;
      c.height = canvasH;
      const ctx = c.getContext('2d');

      // Center the pin horizontally within the wider canvas
      const offsetX = Math.floor((canvasW - pinW) / 2);

      // draw pin shifted down by extraTop so badge can sit above
      ctx.save();
      ctx.translate(offsetX, extraTop);

      if (baseUrl) {
        try {
          const img = await loadImage(baseUrl);
          ctx.drawImage(img, 0, 0, pinW, pinH);
        } catch { /* fallback shape */ }
      }
      if (!baseUrl) {
        ctx.fillStyle = '#2563EB';
        ctx.beginPath();
        ctx.moveTo(pinW/2, pinH);
        ctx.quadraticCurveTo(pinW*0.95, pinH*0.62, pinW*0.95, pinH*0.36);
        ctx.arc(pinW/2, pinH*0.36, pinW*0.45, 0, Math.PI, true);
        ctx.quadraticCurveTo(pinW*0.05, pinH*0.62, pinW/2, pinH);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(pinW/2, pinH*0.32, pinW*0.24, 0, Math.PI*2);
        ctx.fill();
      }

      ctx.restore();

      // draw badge in the top padding area (centered, no width clamp)
      if (text) {
        ctx.font = font;
        const bw = desiredBadgeW;                 // use full measured width
        const bx = Math.round((canvasW - bw) / 2);
        const by = 3;
        const r = 12;
        const bhLocal = bh;

        ctx.fillStyle = '#6366f1';
        ctx.beginPath();
        ctx.moveTo(bx + r, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + bhLocal, r);
        ctx.arcTo(bx + bw, by + bhLocal, bx, by + bhLocal, r);
        ctx.arcTo(bx, by + bhLocal, bx, by, r);
        ctx.arcTo(bx, by, bx + bw, by, r);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.textBaseline = 'middle';
        const textX = Math.round((canvasW - measuredTextW) / 2);
        ctx.fillText(text, textX, by + bhLocal / 2);
      }

      return {
        url: c.toDataURL('image/png'),
        size: { w: canvasW, h: canvasH },
        anchor: { x: Math.round(offsetX + pinW / 2), y: 56 + extraTop } // keep tip anchored
      };
    }

    async function initMap() {
      const map = new google.maps.Map(document.getElementById('map'), {
        center: { lat: 0, lng: 0 }, zoom: 5, mapTypeControl: true, streetViewControl: false,
        ...(MAP_ID ? { mapId: MAP_ID } : {})
      });

      const { Place, Autocomplete } = await google.maps.importLibrary('places');

      // Try Advanced Markers only when a Map ID is provided
      let AdvancedMarkerElement, PinElement, canAdvanced = false;
      if (MAP_ID) {
        try {
          const markerLib = await google.maps.importLibrary('marker');
          AdvancedMarkerElement = markerLib.AdvancedMarkerElement;
          PinElement = markerLib.PinElement;
          canAdvanced = !!AdvancedMarkerElement;
        } catch (e) { canAdvanced = false; }
      }

      const info = new google.maps.InfoWindow();
      const bounds = new google.maps.LatLngBounds();
      const listEl = document.getElementById('list');
      const markers = [];

      function addToList(detail, pid, markerIdx) {
        const name = detail?.displayName || detail?.name || detail?.displayName?.text || 'Place';
        const addr = detail?.formatted_address || detail?.formattedAddress || '';
        // sort badges by day, then AM before PM
        const sortedMeta = (META[pid] || []).slice().sort((a, b) => {
          if (a.day !== b.day) return a.day - b.day;
          if (a.slot === b.slot) return 0;
          return a.slot === 'AM' ? -1 : 1;
        });
        const tags = sortedMeta
          .map(m => '<span class="badge ' + (m.slot === 'AM' ? 'am' : 'pm') + '"><span class="dot"></span>' + 'Day ' + esc(m.day) + ' — ' + (m.slot === 'AM' ? 'AM Activity' : 'PM Activity') + (m.title ? ' — ' + esc(m.title) : '') + '</span>')
          .join('');
        const div = document.createElement('div');
        div.className = 'item';
        div.innerHTML = '<div class="title">' + esc(name) + '</div>' + (addr ? '<div class="sub">' + esc(addr) + '</div>' : '') + (tags ? '<div class="badges">' + tags + '</div>' : '');
        div.onclick = () => {
          const marker = markers[markerIdx]; if (!marker) return;
          const pos = marker.position || marker.getPosition();
          map.panTo(pos); map.setZoom(14);
          info.setContent(createInfoContent(detail)); info.open({ map, anchor: marker });
        };
        listEl.appendChild(div);
      }

      let resolved = 0;

      // sort places by the lowest day present in META (no-meta goes last), then by name
      function minDayFor(pid) {
        const arr = META[pid] || [];
        if (!arr.length) return Number.POSITIVE_INFINITY;
        return arr.reduce((min, m) => Math.min(min, Number(m.day) || Number.POSITIVE_INFINITY), Number.POSITIVE_INFINITY);
      }
      const sortedPlaces = PLACES.slice().sort((a, b) => {
        const da = minDayFor(a.googlePlaceId);
        const db = minDayFor(b.googlePlaceId);
        if (da !== db) return da - db;
        const na = (a.name || '').toString();
        const nb = (b.name || '').toString();
        return na.localeCompare(nb);
      });

      sortedPlaces.forEach((p, i) => {
        setTimeout(async () => {
          try {
            const place = new Place({ id: p.googlePlaceId });
            await place.fetchFields({ fields: ['id','displayName','location','formattedAddress','photos','rating','userRatingCount'] });
            if (place?.location) {
              const position = place.location;
              const title = (place.displayName && (place.displayName.text || place.displayName)) || p.name || 'Attraction';

              const meta = overlayMeta(p.googlePlaceId);
              let marker;

              if (canAdvanced) {
                if (ICON_URL) {
                  const el = document.createElement('div');
                  el.className = 'gti-pin';
                  el.style.backgroundImage = 'url(' + ICON_URL + ')';
                  if (meta.text) {
                    const b = document.createElement('div');
                    b.className = 'gti-pin-badge';
                    b.textContent = meta.text;
                    el.appendChild(b);
                  }
                  marker = new AdvancedMarkerElement({ map, position, title, content: el });
                } else {
                  const pin = new PinElement({
                    glyph: meta.glyph || '',
                    background: '#6366f1',
                    borderColor: '#ffffff',
                    glyphColor: '#ffffff',
                    scale: 1.1
                  });
                  // wrap PinElement to place badge ABOVE the pin
                  const wrap = document.createElement('div');
                  wrap.className = 'gti-pin';
                  wrap.style.width = '44px';
                  wrap.style.height = '58px';
                  pin.element.style.width = '100%';
                  pin.element.style.height = '100%';
                  wrap.appendChild(pin.element);
                  if (meta.text) {
                    const b = document.createElement('div');
                    b.className = 'gti-pin-badge';
                    b.textContent = meta.text;
                    wrap.appendChild(b);
                  }
                  marker = new AdvancedMarkerElement({ map, position, title, content: wrap });
                }
              } else {
                // Fallback: canvas with top padding so badge hovers above
                const composed = await composeIcon(ICON_URL || '', meta);
                marker = new google.maps.Marker({
                  map, position, title,
                  icon: {
                    url: composed.url,
                    scaledSize: new google.maps.Size(composed.size.w, composed.size.h),
                    anchor: new google.maps.Point(composed.anchor.x, composed.anchor.y)
                  }
                });
              }

              const clickEvt = marker.addListener ? 'click' : 'gmp-click';
              marker.addListener(clickEvt, () => { info.setContent(createInfoContent(place)); info.open({ map, anchor: marker }); });

              markers.push(marker); bounds.extend(position);
              addToList(place, p.googlePlaceId, markers.length - 1);
            }
          } finally {
            resolved++;
            if (resolved === sortedPlaces.length) {
              if (!bounds.isEmpty()) map.fitBounds(bounds);
              if (markers.length > 0 && window.markerClusterer?.MarkerClusterer) {
                new window.markerClusterer.MarkerClusterer({ map, markers });
              }
            }
          }
        }, i * 120);
      });

      document.getElementById('showAllBtn').onclick = () => { if (!bounds.isEmpty()) map.fitBounds(bounds); };

      const input = document.getElementById('search');
      const ac = new Autocomplete(input, { fields: ['id','displayName','location'] });
      ac.addListener('place_changed', () => {
        const sel = ac.getPlace?.() || null;
        const loc = sel?.geometry?.location || sel?.location;
        if (!loc) return; map.panTo(loc); map.setZoom(14);
      });
    }
    window.initMap = initMap;
  </script>
  <script async defer src="https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=initMap"></script>
  <script async src="https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js"></script>
</body>
</html>`);
  doc.close();
}

export default function ItineraryMap(props: ItineraryMapProps) {
  const { itinerary, iconUrl, buttonLabel = 'Open Map', className, disabled } = props;

  // Build ids + meta keyed by attractionId (DB id)
  const { ids, metaByAttractionId } = useMemo(() => {
    const idsSet = new Set<string>();
    const metaMap: Record<string, BadgeMeta[]> = {};
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (Array.isArray(itinerary)) {
      itinerary.forEach((d) => {
        const day = d.day ?? 0;
        if (d.amAttractionId && uuidRe.test(d.amAttractionId)) {
          idsSet.add(d.amAttractionId);
          (metaMap[d.amAttractionId] ||= []).push({ day, slot: 'AM', title: d.amActivity || null });
        }
        if (d.pmAttractionId && uuidRe.test(d.pmAttractionId)) {
          idsSet.add(d.pmAttractionId);
          (metaMap[d.pmAttractionId] ||= []).push({ day, slot: 'PM', title: d.pmActivity || null });
        }
      });
    }
    return { ids: Array.from(idsSet), metaByAttractionId: metaMap };
  }, [itinerary]);

  const { loading, error, data } = useQuery(GET_ATTRACTIONS_BY_IDS, {
    variables: { ids },
    skip: ids.length === 0,
    fetchPolicy: 'cache-first',
  });

  // Places list from query
  const places: Place[] = useMemo(() => {
    const list = data?.attractionByIds || [];
    return (list || [])
      .filter((p) => p && p.googlePlaceId)
      .map((p) => ({
        id: p.id,
        name: p.nameDisplay || p.nameLatin || undefined,
        googlePlaceId: p.googlePlaceId as string,
      }));
  }, [data]);

  // REMAP: meta keyed by googlePlaceId so the panel can find it
  const metaByPlaceId = useMemo(() => {
    const map: Record<string, BadgeMeta[]> = {};
    const list = data?.attractionByIds || [];
    for (const p of list) {
      const meta = metaByAttractionId[p.id];
      if (meta && p.googlePlaceId) map[p.googlePlaceId as string] = meta;
    }
    return map;
  }, [data, metaByAttractionId]);

  const resolvedIconUrl = useMemo(() => {
    if (!iconUrl) return undefined;
    try {
      return new URL(iconUrl, window.location.origin).href;
    } catch {
      return iconUrl;
    }
  }, [iconUrl]);

  const onOpen = useCallback(() => {
    if (!ids.length) {
      alert('No attraction IDs provided.');
      return;
    }
    if (!places.length) {
      alert('No matching Google Places found for the provided attraction IDs.');
      return;
    }
    openGoogleMapInNewTab(places, GOOGLE_API_KEY, resolvedIconUrl, 'Itinerary', metaByPlaceId, GOOGLE_MAP_ID); // pass mapId
  }, [ids, places, resolvedIconUrl, metaByPlaceId]);

  if (loading) return <LoadingData title="Loading Attractions..." />;
  if (error) return <GraphqlError error={error} />;

  return (
    <Button
      variant="filled"
      mb={10}
      onClick={onOpen}
      className={className}
      disabled={disabled || places.length === 0}
      aria-busy={false}
    >
      {buttonLabel}
    </Button>
  );
}
